import * as XLSX from "xlsx"
import type { ItemListSummaryRow } from "../types/listItemSummary"
import {
  computeKpi,
  formatNumberId,
  inventoryValue,
  marginPercent
} from "./listItemSummaryHelpers"

export type ExportTableRow = (string | number)[]

export function buildExportRows(rows: ItemListSummaryRow[]): ExportTableRow[] {
  return rows.map((item, index) => {
    const invVal = inventoryValue(item)
    const margin =
      item.sellingPrice && marginPercent(item) !== null
        ? `${marginPercent(item)!.toFixed(1)}%`
        : "-"
    return [
      index + 1,
      item.itemCode,
      item.itemName,
      item.category,
      item.location,
      item.uom,
      item.soh,
      item.avgCost,
      item.lastPrice,
      item.sellingPrice,
      invVal,
      margin,
      item.stockStatus,
      item.movementStatus
    ]
  })
}

export const EXPORT_HEADERS = [
  "No",
  "Item Code",
  "Item Name",
  "Category",
  "Location",
  "UOM",
  "SOH",
  "Average Cost",
  "Last Price",
  "Selling Price",
  "Inventory Value",
  "Margin %",
  "Stock Status",
  "Movement Status"
]

export function exportItemListSummaryXlsx(params: {
  periodLabel: string
  rows: ItemListSummaryRow[]
  fileName?: string
}) {
  const { periodLabel, rows, fileName } = params
  const kpi = computeKpi(rows)
  const body = buildExportRows(rows)

  const worksheetData: (string | number)[][] = [
    ["Inventory Report"],
    [`Item List Summary | Period: ${periodLabel}`],
    [],
    [
      "Total SKU",
      "Total SOH",
      "Inventory Value",
      "Low Stock",
      "Out of Stock",
      "Dead Stock Value",
      "Avg Margin"
    ],
    [
      String(kpi.totalSku),
      formatNumberId(kpi.totalSoh),
      formatNumberId(kpi.inventoryValueTotal),
      String(kpi.lowStock),
      String(kpi.outOfStock),
      formatNumberId(kpi.deadStockValue),
      kpi.avgMarginPct !== null ? `${kpi.avgMarginPct.toFixed(0)}%` : "-"
    ],
    [],
    EXPORT_HEADERS,
    ...body
  ]

  const ws = XLSX.utils.aoa_to_sheet(worksheetData)
  ws["!cols"] = [
    { wch: 6 },
    { wch: 14 },
    { wch: 38 },
    { wch: 20 },
    { wch: 18 },
    { wch: 10 },
    { wch: 10 },
    { wch: 16 },
    { wch: 16 },
    { wch: 16 },
    { wch: 18 },
    { wch: 12 },
    { wch: 18 },
    { wch: 18 }
  ]

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, "Item List Summary")
  XLSX.writeFile(wb, fileName ?? "Inventory_Item_List_Summary.xlsx")
}

export function printItemListSummaryHtml(params: {
  periodLabel: string
  rows: ItemListSummaryRow[]
  orientation: "portrait" | "landscape"
}): Window | null {
  const { periodLabel, rows, orientation } = params
  const kpi = computeKpi(rows)
  const headers = EXPORT_HEADERS
  const exportRows = buildExportRows(rows)

  const pageSize = orientation === "landscape" ? "landscape" : "portrait"
  const fontSize = orientation === "landscape" ? "9px" : "7px"

  const html = `<!DOCTYPE html>
<html>
<head>
  <title>Inventory Report Print</title>
  <style>
    @page { size: A4 ${pageSize}; margin: 10mm; }
    body { font-family: Arial, Helvetica, sans-serif; color: #111; }
    h1 { font-size: 20px; margin: 0; }
    .subtitle { font-size: 12px; margin: 4px 0 12px 0; }
    .kpi { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
    .kpi th { background: #050505; color: white; font-size: 9px; padding: 5px; border: 1px solid #999; }
    .kpi td { text-align: center; font-weight: 800; font-size: 13px; padding: 6px; border: 1px solid #999; }
    table.report { width: 100%; border-collapse: collapse; table-layout: fixed; }
    table.report th { background: #050505; color: white; padding: 4px; border: 1px solid #777; font-size: ${fontSize}; }
    table.report td { padding: 4px; border: 1px solid #999; font-size: ${fontSize}; word-wrap: break-word; }
    .right { text-align: right; }
  </style>
</head>
<body>
  <h1>Inventory Report</h1>
  <div class="subtitle">Item List Summary | Period: ${periodLabel}</div>

  <table class="kpi">
    <tr><th>Total SKU</th><th>Total SOH</th><th>Inventory Value</th><th>Low Stock</th><th>Out of Stock</th><th>Dead Stock Value</th><th>Avg Margin</th></tr>
    <tr>
      <td>${kpi.totalSku}</td>
      <td>${formatNumberId(kpi.totalSoh)}</td>
      <td>${formatNumberId(kpi.inventoryValueTotal)}</td>
      <td>${kpi.lowStock}</td>
      <td>${kpi.outOfStock}</td>
      <td>${formatNumberId(kpi.deadStockValue)}</td>
      <td>${kpi.avgMarginPct !== null ? `${kpi.avgMarginPct.toFixed(0)}%` : "-"}</td>
    </tr>
  </table>

  <table class="report">
    <thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead>
    <tbody>
      ${exportRows
        .map(
          (row) => `
        <tr>
          ${row
            .map((cell, i) =>
              [6, 7, 8, 9, 10, 11].includes(i)
                ? `<td class="right">${cell}</td>`
                : `<td>${cell}</td>`
            )
            .join("")}
        </tr>`
        )
        .join("")}
    </tbody>
  </table>
</body>
</html>`

  const w = window.open("", "_blank", "noopener,noreferrer")
  if (!w) return null
  w.document.open()
  w.document.write(html)
  w.document.close()
  w.focus()
  setTimeout(() => w.print(), 300)
  return w
}
